import { useEffect, useState } from 'react';

export const SHARED_FORECAST_FILTERS_KEY = 'shared_forecast_filters';

export const DEFAULT_FORECAST_FILTERS = {
  brand: '',
  sku_id: '',
  channel: '',
  location: '',
  region: '',
  start_date: '',
  end_date: '',
  forecast_days: 28,
  granularity_level: 'sku_channel_location',
  forecast_granularity: 'daily',
  promo_modeling_approach: 'separated',
  include_base_demand: true,
  include_promotional_demand: true,
  promo_type_filter: '',
  level: 'brand_day',
  customer: '',
};

const safeParse = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (err) {
    console.warn('Failed to parse shared forecast filters:', err);
    return null;
  }
};

const loadLegacyFilters = (legacyKeys = []) => {
  if (typeof window === 'undefined') return null;
  for (const key of legacyKeys) {
    const parsed = safeParse(window.localStorage.getItem(key));
    if (parsed && typeof parsed === 'object') {
      return parsed;
    }
  }
  return null;
};

const loadInitialFilters = (legacyKeys = []) => {
  if (typeof window === 'undefined') {
    return DEFAULT_FORECAST_FILTERS;
  }

  const shared = safeParse(window.localStorage.getItem(SHARED_FORECAST_FILTERS_KEY));
  if (shared && typeof shared === 'object') {
    return { ...DEFAULT_FORECAST_FILTERS, ...shared };
  }

  const legacy = loadLegacyFilters(legacyKeys);
  if (legacy && typeof legacy === 'object') {
    return { ...DEFAULT_FORECAST_FILTERS, ...legacy };
  }

  return DEFAULT_FORECAST_FILTERS;
};

const useSharedForecastFilters = (legacyKeys = []) => {
  const [value, setValue] = useState(() => loadInitialFilters(legacyKeys));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(SHARED_FORECAST_FILTERS_KEY, JSON.stringify(value));
      window.dispatchEvent(new CustomEvent('shared_forecast_filters_updated', { detail: value }));
    } catch (err) {
      console.warn('Failed to persist shared forecast filters:', err);
    }
  }, [value]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncFromStorage = () => {
      const shared = safeParse(window.localStorage.getItem(SHARED_FORECAST_FILTERS_KEY));
      if (shared && typeof shared === 'object') {
        setValue(prev => {
          const next = { ...DEFAULT_FORECAST_FILTERS, ...shared };
          const same = Object.keys(next).every(key => next[key] === prev[key]);
          return same ? prev : next;
        });
      }
    };

    const handleCustomSync = (event) => {
      const shared = event?.detail;
      if (shared && typeof shared === 'object') {
        setValue(prev => {
          const next = { ...DEFAULT_FORECAST_FILTERS, ...shared };
          const same = Object.keys(next).every(key => next[key] === prev[key]);
          return same ? prev : next;
        });
      } else {
        syncFromStorage();
      }
    };

    window.addEventListener('storage', syncFromStorage);
    window.addEventListener('shared_forecast_filters_updated', handleCustomSync);
    return () => {
      window.removeEventListener('storage', syncFromStorage);
      window.removeEventListener('shared_forecast_filters_updated', handleCustomSync);
    };
  }, []);

  return [value, setValue];
};

export default useSharedForecastFilters;
